import type { FieldTag } from './FieldTag';

export type FieldSensitivity = 'public' | 'internal' | 'manager' | 'hr_confidential' | 'hr_restricted' | 'sensitive_pii';

export enum FieldType {
  TEXT = 'text',
  NUMBER = 'number',
  DATE = 'date',
  SELECT = 'select',
  MULTI_SELECT = 'multiSelect',
  BOOLEAN = 'boolean',
  RICH_TEXT = 'richText',
  COMPOSITE = 'composite',
}

export interface ValidationRule {
  type: 'required' | 'min' | 'max' | 'pattern' | 'custom';
  value?: unknown;
  message?: string;
  validator?: (value: unknown) => boolean | Promise<boolean>;
}

export interface ValidationRules {
  rules: ValidationRule[];
}

export interface VisibilityCondition {
  type: 'always' | 'fieldValue' | 'tag' | 'custom';
  field?: string;
  value?: unknown;
  operator?: 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan';
  evaluator?: (context: Record<string, unknown>) => boolean;
}

export interface FieldOption {
  value: string;
  label: string;
  color?: string;
  icon?: string;
}

export interface CompositeFieldDefinition {
  [key: string]: FieldManifest;
}

export interface FieldManifest {
  id: string;
  label: string;
  description?: string;
  helpText?: string;
  type: FieldType;
  tags: string[] | FieldTag[];
  defaultValue?: unknown;
  placeholder?: string;
  required?: boolean;
  validation?: ValidationRules;
  visibility?: VisibilityCondition;
  priority?: number;
  readonly?: boolean;
  options?: FieldOption[];
  fields?: CompositeFieldDefinition;
  namespace?: string;
  allowCustomOptions?: boolean;
  multiline?: boolean;
  maxLength?: number;
  minLength?: number;
  min?: number;
  max?: number;
  step?: number;
  format?: string;
  sensitivity?: FieldSensitivity;
  /** Registered pack used by folder-data grants. Custom fields must set this before protected use. */
  fieldPackId?: string;
  /** Values are excluded from Yjs unless this is explicitly true and sensitivity is below manager. */
  yjsSafe?: boolean;
  /** Source-authority lanes able to supply this field. */
  sourceTypes?: string[];
  metadata?: Record<string, unknown>;
}

export type FieldValue = string | number | boolean | Date | unknown[] | Record<string, unknown> | null | undefined;

export interface FieldError {
  field: string;
  message: string;
  rule?: string;
}

export interface FieldValidationResult {
  valid: boolean;
  errors: FieldError[];
}
